import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { PaginatedResponse } from '@/modules/common/types/pagination-types';

const DEFAULT_STALE_POLL_INTERVAL_MS = 30_000;

interface UsePollingStalenessArgs<TItem> {
  /** Disable polling (e.g. no filters yet) */
  enabled: boolean;
  /** Stable hash of the applied filters — used to reset the baseline */
  filterFingerprint: string;
  /**
   * Items currently shown to the user (from the authoritative list query).
   * Used to seed the staleness baseline so the hook never fires a redundant
   * request for data the page already loaded.
   */
  authoritativeItems: TItem[] | undefined;
  /** Query key for the polling peek cache (used by markAccepted consumers) */
  peekQueryKey: readonly unknown[];
  /** Fetches the latest server snapshot */
  fetchPeek: () => Promise<PaginatedResponse<TItem>>;
  /** Stable structural hash of the items */
  hashItems: (items: TItem[] | undefined) => string;
  intervalMs?: number;
  /**
   * Optional increasing backoff schedule (ms per tick). When provided it overrides
   * `intervalMs`: the poll fires after `schedule[0]`, then `schedule[1]`, etc., and
   * repeats the last entry once the schedule is exhausted. Pass a STABLE (module-level)
   * array — a fresh array each render restarts the timer. Reset to the first step on
   * filter change / re-enable (the effect re-runs on those deps).
   */
  intervalSchedule?: number[];
}

interface UsePollingStalenessReturn<TItem> {
  hasStaleData: boolean;
  /** Items from the most recent poll; null until the first poll responds. */
  latestPeekRef: RefObject<TItem[] | null>;
  /** Call after merging fresh data into the cache to clear the stale flag */
  markAccepted: (items: TItem[]) => void;
}

/**
 * Background-polls a list endpoint and exposes a `hasStaleData` flag that flips
 * when the server's representation diverges from what the user is looking at.
 *
 * Key invariant: this hook never duplicates the authoritative list request.
 * The baseline is taken directly from `authoritativeItems` (the data the list
 * query already loaded), and the first poll only fires one interval *after*
 * that data arrives — not on mount. A separate "peek" query would otherwise
 * race the authoritative query and hit the same endpoint twice on every open.
 *
 * The baseline resets whenever `filterFingerprint` changes — a filter mutation
 * is a fresh load, not a stale-data event, so we mustn't flash the indicator.
 */
export const usePollingStaleness = <TItem extends { id?: number | string }>({
  enabled,
  filterFingerprint,
  authoritativeItems,
  peekQueryKey,
  fetchPeek,
  hashItems,
  intervalMs = DEFAULT_STALE_POLL_INTERVAL_MS,
  intervalSchedule,
}: UsePollingStalenessArgs<TItem>): UsePollingStalenessReturn<TItem> => {
  const queryClient = useQueryClient();
  const [hasStaleData, setHasStaleData] = useState(false);
  const acceptedHashRef = useRef<string | null>(null);
  const latestPeekRef = useRef<TItem[] | null>(null);

  // Keep the latest fetchPeek / hashItems in refs so the polling effect doesn't
  // restart (and re-poll) on every render when these identities change.
  const fetchPeekRef = useRef(fetchPeek);
  fetchPeekRef.current = fetchPeek;
  const hashItemsRef = useRef(hashItems);
  hashItemsRef.current = hashItems;
  const peekQueryKeyRef = useRef(peekQueryKey);
  peekQueryKeyRef.current = peekQueryKey;

  // Reset the baseline on filter change — the next authoritative load reseeds it.
  useEffect(() => {
    acceptedHashRef.current = null;
    latestPeekRef.current = null;
    setHasStaleData(false);
  }, [filterFingerprint]);

  // Seed the baseline from the data the page is already showing. No request.
  useEffect(() => {
    if (acceptedHashRef.current !== null) return;
    if (!authoritativeItems) return;
    acceptedHashRef.current = hashItemsRef.current(authoritativeItems);
  }, [authoritativeItems, filterFingerprint]);

  // Poll for staleness on an interval — but only once we have a baseline, so the
  // first poll happens one interval after the authoritative load, never on mount.
  const hasBaseline = authoritativeItems !== undefined;
  useEffect(() => {
    if (!enabled || !hasBaseline) return;

    let cancelled = false;

    const recordSnapshot = (snapshot: PaginatedResponse<TItem>) => {
      latestPeekRef.current = snapshot.items;
      queryClient.setQueryData(peekQueryKeyRef.current, snapshot);

      const nextHash = hashItemsRef.current(snapshot.items);
      if (acceptedHashRef.current === null) {
        // No baseline yet (e.g. reset by a filter change mid-poll) — adopt this snapshot.
        acceptedHashRef.current = nextHash;
      } else if (nextHash !== acceptedHashRef.current) {
        setHasStaleData(true);
      }
    };

    const poll = async () => {
      try {
        const snapshot = await fetchPeekRef.current();
        if (cancelled || !snapshot?.items) return;
        recordSnapshot(snapshot);
      } catch {
        // Polling is best-effort; a failed peek shouldn't surface an error.
      }
    };

    // Fixed interval, or a backoff schedule that advances per tick and repeats its last
    // entry. Restarts from step 0 when the effect re-runs (filter change / re-enable).
    const schedule = intervalSchedule?.length ? intervalSchedule : [intervalMs];
    let stepIndex = 0;
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      const delay = schedule[Math.min(stepIndex, schedule.length - 1)];
      timeoutId = setTimeout(async () => {
        await poll();
        if (cancelled) return;
        stepIndex += 1;
        scheduleNext();
      }, delay);
    };
    scheduleNext();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [enabled, hasBaseline, filterFingerprint, intervalMs, intervalSchedule, queryClient]);

  const markAccepted = useCallback((items: TItem[]) => {
    acceptedHashRef.current = hashItemsRef.current(items);
    setHasStaleData(false);
  }, []);

  return { hasStaleData, latestPeekRef, markAccepted };
};
